import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { flashcardsService } from '../services/flashcardsService';
import { collectionsService } from '../services/collectionsService';
import { statsService } from '../services/statsService';
import { 
  ArrowRightIcon, 
  CheckCircleIcon, 
  XCircleIcon 
} from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';

interface QuizState {
  currentIndex: number;
  answers: number[];
  isAnswered: boolean;
  score: number;
  startTime: number;
}

export const QuizTake = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collection, setCollection] = useState<any>(null);
  const [flashcards, setFlashcards] = useState<any[]>([]);
  const [quizState, setQuizState] = useState<QuizState>({
    currentIndex: 0,
    answers: [],
    isAnswered: false,
    score: 0,
    startTime: Date.now(),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionData, flashcardsData] = await Promise.all([
          collectionsService.getCollection(id!),
          flashcardsService.getFlashcards(id!),
        ]);
        setCollection(collectionData);
        setFlashcards(shuffleArray(flashcardsData));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load quiz');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswer = (answerIndex: number) => {
    if (quizState.isAnswered) return;

    const currentFlashcard = flashcards[quizState.currentIndex];
    const isCorrect = answerIndex === currentFlashcard.correctAnswer;
    
    setQuizState(prev => ({
      ...prev,
      answers: [...prev.answers, answerIndex],
      isAnswered: true,
      score: isCorrect ? prev.score + 1 : prev.score,
    }));
  };

  const calculateDifficulty = (score: number, total: number): 'easy' | 'medium' | 'hard' => {
    const percentage = (score / total) * 100;
    if (percentage >= 80) return 'easy';
    if (percentage >= 60) return 'medium';
    return 'hard';
  };

  const handleNext = async () => {
    if (quizState.currentIndex === flashcards.length - 1) {
      // Calculate study time in minutes
      const endTime = Date.now();
      const studyTimeMinutes = Math.round((endTime - quizState.startTime) / 60000);
      const validStudyTime = Math.max(1, studyTimeMinutes);
      
      // Only update statistics once
      try {
        const score = Math.round((quizState.score / flashcards.length) * 100);
        const difficulty = calculateDifficulty(quizState.score, flashcards.length);
        
        // Use a single stats update call with both quiz and study session data
        await statsService.updateQuizStats(
          score,
          difficulty,
          validStudyTime
        );
      } catch (error) {
        console.error('Failed to update statistics:', error);
      }

      // Navigate to results
      navigate(`/quiz/${id}/results`, { 
        state: { 
          score: quizState.score,
          total: flashcards.length,
          answers: quizState.answers,
          flashcards: flashcards,
          studyTime: validStudyTime
        }
      });
      return;
    }

    setQuizState(prev => ({
      ...prev,
      currentIndex: prev.currentIndex + 1,
      isAnswered: false,
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <NavigationBar />
        <div className="pt-20 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 dark:border-indigo-400"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <NavigationBar />
        <div className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Error</h2>
            <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => navigate('/quizzes')}
              className="mt-4 px-4 py-2 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Back to Collections
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (flashcards.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <NavigationBar />
        <div className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">No Flashcards Found</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">This collection doesn't have any flashcards yet.</p>
            <button
              onClick={() => navigate(`/collections/${id}`)}
              className="mt-4 px-4 py-2 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Add Flashcards
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentFlashcard = flashcards[quizState.currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-indigo-50 dark:from-indigo-900/20 opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_20%,white_70%)] dark:bg-[radial-gradient(circle_at_center,transparent_20%,#111827_70%)]"></div>
        <svg className="absolute left-0 right-0 w-full" style={{ top: '100%', transform: 'translateY(-50%)' }}>
          <defs>
            <linearGradient id="wave" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(99, 102, 241, 0.1)" />
              <stop offset="100%" stopColor="rgba(168, 85, 247, 0.1)" />
            </linearGradient>
          </defs>
          <path
            fill="url(#wave)"
            d="M0,0 C300,100 400,100 500,0 C600,-100 700,-100 800,0 L800,100 L0,100 Z"
            className="animate-wave"
          />
        </svg>
      </div>

      <NavigationBar />
      <div className="pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{collection.title}</h1>
            <div className="mt-2 flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Question {quizState.currentIndex + 1} of {flashcards.length}
              </p>
              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                Score: {quizState.score} / {flashcards.length}
              </p>
            </div>
          </div>

          <motion.div
            key={quizState.currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {currentFlashcard.question}
            </h2>

            <div className="space-y-3">
              {currentFlashcard.options.map((option: string, index: number) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAnswer(index)}
                  disabled={quizState.isAnswered}
                  className={`w-full p-4 text-left rounded-lg border transition-all ${
                    !quizState.isAnswered
                      ? 'border-gray-200 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-gray-900 dark:text-white'
                      : index === currentFlashcard.correctAnswer
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : index === quizState.answers[quizState.currentIndex]
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 opacity-50'
                  }`}
                >
                  <div className="flex items-center">
                    {quizState.isAnswered && index === currentFlashcard.correctAnswer && (
                      <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                    )}
                    {quizState.isAnswered && 
                      index === quizState.answers[quizState.currentIndex] && 
                      index !== currentFlashcard.correctAnswer && (
                      <XCircleIcon className="h-5 w-5 mr-2 text-red-500" />
                    )}
                    <span>{option}</span>
                  </div>
                </motion.button>
              ))}
            </div>

            {quizState.isAnswered && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex justify-end"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNext}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 dark:from-indigo-600 dark:to-purple-700 dark:hover:from-indigo-700 dark:hover:to-purple-800"
                >
                  {quizState.currentIndex === flashcards.length - 1 ? (
                    'See Results'
                  ) : (
                    <>
                      Next Question
                      <ArrowRightIcon className="ml-2 h-5 w-5" />
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}
          </motion.div>

          <div className="mt-6">
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-400 dark:to-purple-500 transition-all duration-500"
                style={{ width: `${((quizState.currentIndex + 1) / flashcards.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};